import jsonPackage from '../../../../../package.json';
import { environment } from '../../../../environments/environment';
import { Injectable } from '@angular/core';
import { SessionState, SessionStore } from './session.store';
import { SessionQuery } from '@state/session/session.query';
import { HttpClient } from '@angular/common/http';
import {
    AES_URL_STORAGE_KEY, AGENDA_DISPLAY_STORAGE_KEY,
    ERROR_CALL_MAPPER,
    FLOWWWME_STATUS, PASSWORD_STORAGE_KEY,
    SESSION_STORAGE_KEY,
    USERNAME_STORAGE_KEY,
    OTP_HASH_KEY,
    OTP_HASH_DATE
} from '@core/constants';
import { switchMap, tap, map } from 'rxjs/operators';
import { ToastService } from '@core/services/toast/toast.service';
import { LibraryService } from '@core/services/library/library.service';
import { AesRequestModel } from '@core/models/AesRequest.model';
import { Observable } from 'rxjs';
import { resetStores } from '@datorama/akita';
import { NavController } from '@ionic/angular';
import * as Crypto from 'crypto-js';
import { ModalService } from '@core/services/modal/modal.service';
import { ApiService } from '@core/services/api/api.service';

import { Preferences }  from '@capacitor/preferences';
import { catchError } from 'rxjs/operators';
import { SettingsStore } from '../settings/settings.store';
@Injectable({ providedIn: 'root' })
export class SessionService {
    private DEF_XHR_GATEWAY_URL: string;

    private ME_MAPPER_URL: string;

    constructor(
        private apiService: ApiService,
        private http: HttpClient,
        private libraryService: LibraryService,
        private modalService: ModalService,
        private navController: NavController,
        private sessionStore: SessionStore,
        private sessionQuery: SessionQuery,
        private toastService: ToastService,
        private settingsStore: SettingsStore
    ) {
    }

    refreshConnection(): Observable<string | void> {
        const form = new AesRequestModel('SEC_REFRESH_FORM', []);

        return this.apiService.callApiObservable(form);
    }
//Antiguo login, ahora siempre usamos otpSystemLogin aunque no tengamos OTP
    systemLogin(email: string, password: string): Observable<any> {
        this.sessionStore.update({ sessionId: '' });

        const url = `${this.ME_MAPPER_URL}?email=${email}&ver=${jsonPackage.version.replace(/\./g, '')}`;
        return this.http.get(url).pipe(
            switchMap((response: any) => {
                if (response && response.URL && response.MeStatus) {
                    if (response.MeStatus === FLOWWWME_STATUS.OBSOLETE) {
                        const obsoleteLabel = this.libraryService.getLabel('lbl_flowww_new_version');
                        this.toastService.presentErrorToast(obsoleteLabel);

                        throw new Error('Obsolete version');
                    } else {
                        let XHRGateway = response.URL ? response.URL : this.DEF_XHR_GATEWAY_URL;
                        this.sessionStore.update({
                            aesUrl: `${XHRGateway}?url=${encodeURIComponent(response.URL)}`
                        });
                    }
                } else {
                    const errorLabel = this.libraryService.getLabel('lbl_login_error');
                    this.toastService.presentErrorToast(errorLabel);

                    throw new Error(ERROR_CALL_MAPPER);
                }

                return this.login(email, password).pipe(
                    tap(async () => {
                        const encryptedPassword = Crypto.AES.encrypt(password, this.sessionQuery.getKey());
                        Preferences.set({ key: AES_URL_STORAGE_KEY, value: this.sessionQuery.getValue().aesUrl });
                        Preferences.set({ key: USERNAME_STORAGE_KEY, value: email });
                        Preferences.set({ key: PASSWORD_STORAGE_KEY, value: '-data-' + encryptedPassword + '-data-' });
                    })
                );
            })
        );
    }

    otpSystemLogin(email: string, password: string, otp: string, otpHash: string, rememberMe: boolean): Observable<any> {
        // Un login manual no debe partir con un sessionId previo: si quedara uno
        // caducado en el store, viajaría como cookie y updateSessionId (que solo
        // escribe con el store vacío) descartaría el de la sesión nueva
        this.sessionStore.update({ sessionId: '' });

        const url = `${this.ME_MAPPER_URL}?email=${email}&ver=${jsonPackage.version.replace(/\./g, '')}`;
        return this.http.get(url).pipe(
            switchMap((response: any) => {
                if (response && response.URL && response.MeStatus) {
                        const SKUrl = response.URL ? response.URL : this.DEF_XHR_GATEWAY_URL;
                        this.sessionStore.update({
                            aesUrl: `${SKUrl}/cgi-bin/dllrequest.asp`
                        });
                } else {
                    const errorLabel = this.libraryService.getLabel('lbl_login_error');
                    this.toastService.presentErrorToast(errorLabel);

                    throw new Error(ERROR_CALL_MAPPER);
                }

                // Un login SIEMPRE genera sesión nueva: limpiar cualquier sessionId
                // previo del store para que updateSessionId (que solo persiste sobre
                // store vacío) capture el header de la respuesta. Si no, el id antiguo
                // se queda y las peticiones posteriores fallan con error de sesión.
                this.sessionStore.update({ sessionId: '' });

                return this.otpLogin(email, password, otp, otpHash).pipe(
                    map(data => JSON.parse(data)),
                    tap( {next: async (res) => {
                        const encryptedPassword = Crypto.AES.encrypt(password, this.sessionQuery.getKey());
                        Preferences.set({ key: AES_URL_STORAGE_KEY, value: this.sessionQuery.getValue().aesUrl });
                        Preferences.set({ key: USERNAME_STORAGE_KEY, value: email });
                        Preferences.set({ key: PASSWORD_STORAGE_KEY, value: '-data-' + encryptedPassword + '-data-' });
                        if(rememberMe){
                            otpHash = res.data.Hash;
                            Preferences.set({ key: `${OTP_HASH_KEY}-${email}`, value: otpHash });
                            const currentDate = new Date();
                            const expirationDate = new Date(currentDate.getTime() + (30 * 24 * 60 * 60 * 1000));
                            Preferences.set({ key: OTP_HASH_DATE, value: expirationDate.toISOString() });
                        }
                    },
            })

                );
            })

        );

    }

    async systemLogout(): Promise<void> {
        // Clear storage but save some fields
        const email = (await Preferences.get({ key: USERNAME_STORAGE_KEY })).value;
        const password = (await Preferences.get({ key: PASSWORD_STORAGE_KEY })).value;
        const agendaDisplay = (await Preferences.get({ key: AGENDA_DISPLAY_STORAGE_KEY })).value;
        const otpHash = (await Preferences.get({ key: `${OTP_HASH_KEY}-${email}` })).value;
        const otpHashDate = (await Preferences.get({ key: OTP_HASH_DATE })).value;
        await Preferences.clear();
        // Solo restaurar valores existentes: en nativo Preferences.set rechaza
        // con value null (p.ej. logout forzado sin haber llegado a loguearse)
        if (email) {
            await Preferences.set({ key: USERNAME_STORAGE_KEY, value: email });
        }
        if (password) {
            await Preferences.set({ key: PASSWORD_STORAGE_KEY, value: password });
        }
        if (agendaDisplay) {
            await Preferences.set({ key: AGENDA_DISPLAY_STORAGE_KEY, value: agendaDisplay });
        }
        if (email && otpHash) {
            await Preferences.set({ key: `${OTP_HASH_KEY}-${email}`, value: otpHash });
        }
        if (otpHashDate) {
            await Preferences.set({ key: OTP_HASH_DATE, value: otpHashDate });
        }

        // Clear authToken from sessionStorage and cookies
        sessionStorage.removeItem('authToken');
        document.cookie = 'authToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';

        // Clear refresh form interval
        clearInterval(this.sessionQuery.getValue().refreshInterval);

        // Reset akita stores
        resetStores();

        // Reset settingsLoaded to false to avoid store cache login errors
        this.settingsStore.update({settingsLoaded: false});

        // Close all modals
        this.modalService.closeTopModal();

        await this.navController.navigateRoot('/login');

        // Tras navegar: recargar las labels offline implica un GET que puede
        // fallar y no debe poder impedir que el logout llegue a /login
        await this.libraryService.removeOnlineTranslations();
    }

    async systemReload(): Promise<void> {
        // Clear refresh form interval
        clearInterval(this.sessionQuery.getValue().refreshInterval);

        // Reset akita stores
        resetStores();

        // Close all modals
        this.modalService.closeTopModal();

        await this.navController.navigateRoot('/login');

        // Tras navegar: recargar las labels offline implica un GET que puede
        // fallar y no debe poder impedir que el reload llegue a /login
        await this.libraryService.removeOnlineTranslations();
    }

    async loadUrlsFile(): Promise<void> {
        const location = (await this.http.get('/assets/location/location.json').toPromise()) as {
            DEF_XHR_GATEWAY_URL: string;
            ME_MAPPER_URL: string;
        };

        if (location) {
            this.DEF_XHR_GATEWAY_URL = location.DEF_XHR_GATEWAY_URL;
            this.ME_MAPPER_URL = location.ME_MAPPER_URL;
        } else {
            this.DEF_XHR_GATEWAY_URL = environment.DEF_XHR_GATEWAY_URL;
            this.ME_MAPPER_URL = environment.ME_MAPPER_URL;
        }
    }

    update(state: Partial<SessionState>): void {
        this.sessionStore.update(state);
    }

    updateSessionId(sessionId: string): void {
        if (!this.sessionQuery.getValue().sessionId) {
            this.sessionStore.update({
                sessionId
            });

            Preferences.set({ key: SESSION_STORAGE_KEY, value: sessionId });
        }
    }

    private login(email: string, password: string): Observable<string> {
        const form = new AesRequestModel('SEC_LOGIN_FORM', [
            { Param: 'User', Value: email, AESIndex: '1' },
            { Param: 'Password', Value: password, AESIndex: '2' },
            { Param: 'ForceLogin', Value: '-1', AESIndex: '3' },
        ]);

        return this.apiService.callApiObservable(form);
    }

    private otpLogin(email: string, password: string, otp: string, otpHash: string): Observable<string> {
        const form = new AesRequestModel('SEC_LOGIN_FORM', [
            { Param: 'User', Value: email, AESIndex: '1' },
            { Param: 'Password', Value: password, AESIndex: '2' },
            { Param: 'ForceLogin', Value: '-1', AESIndex: '3' },
            { Param: 'Otp', Value: otp, AESIndex: '6' },
            { Param: 'OtpCookie', Value: otpHash, AESIndex: '7' },
        ]);

        return this.apiService.callApiObservable(form);
    }

}
